import { useState } from 'react';
import { Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import evictWorkerTasks from 'store/evictWorkerTasks';

interface EvictWorkerTasksDialogProps {
  workerId: string;
  workerName: string;
  // Number of tasks the backend will evict: everything running on a retiring
  // worker, only the surplus above the slot target on a draining one.
  evictCount: number;
  isRetiring: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export default function EvictWorkerTasksDialog({
  workerId,
  workerName,
  evictCount,
  isRetiring,
  onClose,
  onConfirm,
}: EvictWorkerTasksDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await evictWorkerTasks(workerId);
      onConfirm();
      onClose();
    } catch (err) {
      console.error('Failed to evict worker tasks:', err);
      setError('Failed to evict tasks. The worker must be draining or retiring, and on a recent pytailor version.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  const taskPhrase = evictCount === 1 ? 'task' : 'tasks';
  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Evict Running Tasks</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {isRetiring ? (
            <>
              This will stop {evictCount === 1 ? 'the task' : `the ${evictCount} ${taskPhrase}`} currently running on
              retiring worker <strong>{workerName}</strong> and put {evictCount === 1 ? 'it' : 'them'} back in the
              queue, letting the worker exit immediately instead of waiting for long-running tasks to finish.
            </>
          ) : (
            <>
              This will stop {evictCount === 1 ? 'the surplus task' : `the ${evictCount} surplus ${taskPhrase}`} on
              worker <strong>{workerName}</strong> — the work beyond its new slot target — and put{' '}
              {evictCount === 1 ? 'it' : 'them'} back in the queue. Tasks within the target keep running.
            </>
          )}{' '}
          In-progress work on the stopped {taskPhrase} is lost, but {evictCount === 1 ? 'it is' : 'they are'} not
          cancelled — {evictCount === 1 ? 'it' : 'they'} will run again when capacity is available.
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="warning" disabled={isSubmitting}>
          {isSubmitting ? 'Evicting…' : 'Evict tasks'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
